import { useState, type ReactNode } from 'react';
import { Eye, Lock, Mail, Paperclip, PencilLine, RotateCcw } from 'lucide-react';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { RecipientPicker } from '@/components/recipient-picker';
import { Skeleton } from '@/components/skeleton';
import { Textarea } from '@/components/textarea';
import { useToast } from '@/components/toast';
import { cn } from '@/lib/cn';
import {
  greetingFor,
  greetingNameFor,
  openEmailAttachment,
  useEmailDraft,
  useEmailPreview,
  type EmailSource,
  type EmailWording,
} from '@/lib/email-composer';
import { extractMessage, useFormError } from '@/lib/form-error';
import { useRecipientSelection, type ChosenRecipients } from '@/lib/mail-recipients';
import type { EmailDraftData } from '@/types/api';

export type ComposedEmail = ChosenRecipients & EmailWording;

/**
 * Review, and if needed rewrite, an email before it goes out. Opens with the
 * standard subject and message; the preview beside it is rendered by the
 * server from the real template, so it is exactly what the recipient gets.
 *
 * Only the wording is editable. Amounts, the button and its link, the
 * signature and the attachments stay in the template below the message.
 */
export function EmailComposerDialog({
  source,
  title,
  confirmLabel,
  onClose,
  onSend,
  pending,
  summary,
  lockedNote = 'The amount, the button and link, and the signature are added below your message.',
}: {
  source: EmailSource;
  title: string;
  confirmLabel: string;
  onClose: () => void;
  /** Throw to keep the dialog open; the message shows inline. */
  onSend: (email: ComposedEmail, draft: EmailDraftData) => Promise<void>;
  pending: boolean;
  /** What is being sent (a document, an amount), shown above the form. */
  summary?: ReactNode;
  lockedNote?: string;
}) {
  const draft = useEmailDraft(source, true);
  const [chosen, setChosen] = useRecipientSelection(draft.data?.recipients ?? undefined, true);
  const { error, wrap } = useFormError();
  const toast = useToast();

  // An invitation goes to the address typed into the invite form; there is
  // nobody else to pick.
  const fixedTo = source.kind === 'invitation' ? source.fields.email : null;
  const to = fixedTo ? [fixedTo] : chosen.to;
  const cc = fixedTo ? [] : chosen.cc;

  // Until the message is touched it follows the recipient, so picking Marco
  // instead of Dana greets Marco. Once edited, it is the admin's text.
  const greeting = greetingFor(
    source.kind === 'invitation' ? source.fields.name : greetingNameFor(draft.data?.recipients, to),
  );
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const [messageEdit, setMessageEdit] = useState<string | null>(null);
  const subject = subjectEdit ?? draft.data?.subject ?? '';
  const message = messageEdit ?? (draft.data ? `${greeting}\n\n${draft.data.body}` : '');
  const edited = subjectEdit !== null || messageEdit !== null;

  const preview = useEmailPreview(source, { subject, message }, draft.data !== undefined);

  // Phone width shows one pane at a time; wider screens show both.
  const [pane, setPane] = useState<'edit' | 'preview'>('edit');

  async function send() {
    if (!draft.data) return;
    await wrap(() => onSend({ to, cc, subject, message }, draft.data));
  }

  return (
    <Dialog open onClose={onClose} title={title} size="2xl">
      {draft.isError ? (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-danger)]">{extractMessage(draft.error)}</p>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-1 rounded-md bg-[var(--color-surface-muted)] p-1 lg:hidden">
            {(['edit', 'preview'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setPane(key)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium',
                  pane === key
                    ? 'bg-[var(--color-surface-elevated)] text-[var(--color-foreground)] shadow-sm'
                    : 'text-[var(--color-foreground-muted)]',
                )}
              >
                {key === 'edit' ? <PencilLine className="size-3.5" /> : <Eye className="size-3.5" />}
                {key === 'edit' ? 'Edit' : 'Preview'}
              </button>
            ))}
          </div>

          <div className="grid max-h-[56vh] grid-cols-1 gap-5 overflow-y-auto sm:max-h-[64vh] lg:grid-cols-2">
            <div className={cn('min-w-0 space-y-4', pane === 'preview' && 'hidden lg:block')}>
              {summary}

              <Field label="Recipients">
                {fixedTo ? (
                  <div className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm">
                    {source.kind === 'invitation' ? `${source.fields.name} · ` : null}
                    {fixedTo}
                  </div>
                ) : (
                  <RecipientPicker
                    data={draft.data?.recipients ?? undefined}
                    isLoading={draft.isLoading}
                    value={chosen}
                    onChange={setChosen}
                  />
                )}
              </Field>

              <Field label="Subject">
                {draft.data ? (
                  <Input value={subject} onChange={(e) => setSubjectEdit(e.target.value)} />
                ) : (
                  <Skeleton className="h-10 w-full" />
                )}
              </Field>

              <Field
                label="Message"
                action={
                  edited ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSubjectEdit(null);
                        setMessageEdit(null);
                      }}
                      className="flex items-center gap-1 text-xs font-medium normal-case tracking-normal text-[var(--color-primary)] hover:underline"
                    >
                      <RotateCcw className="size-3" /> Reset to standard wording
                    </button>
                  ) : null
                }
              >
                {draft.data ? (
                  <Textarea
                    value={message}
                    onChange={(e) => setMessageEdit(e.target.value)}
                    rows={11}
                    className="font-[inherit] leading-relaxed"
                  />
                ) : (
                  <Skeleton className="h-56 w-full" />
                )}
                <p className="flex items-start gap-1.5 pt-1 text-xs text-[var(--color-foreground-muted)]">
                  <Lock className="mt-0.5 size-3 shrink-0" />
                  <span>{lockedNote} A blank line starts a new paragraph.</span>
                </p>
              </Field>

              {draft.data && draft.data.attachments.length > 0 ? (
                <Field label="Attached">
                  <div className="flex flex-wrap gap-2">
                    {draft.data.attachments.map((file) => (
                      <button
                        key={file.url}
                        type="button"
                        onClick={async () => {
                          try {
                            await openEmailAttachment(file.url);
                          } catch {
                            toast.error('Could not open the attachment');
                          }
                        }}
                        className="flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs hover:bg-[var(--color-surface-muted)]"
                      >
                        <Paperclip className="size-3.5 text-[var(--color-foreground-muted)]" />
                        {file.name}
                      </button>
                    ))}
                  </div>
                </Field>
              ) : null}
            </div>

            <div className={cn('min-w-0 space-y-2', pane === 'edit' && 'hidden lg:block')}>
              <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-muted)]">
                <span>Preview</span>
                {preview.isFetching && preview.data ? (
                  <span className="normal-case tracking-normal text-[var(--color-foreground-subtle)]">Updating…</span>
                ) : null}
              </div>
              <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
                <div className="space-y-0.5 border-b border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-xs">
                  <div className="truncate">
                    <span className="text-[var(--color-foreground-muted)]">Subject: </span>
                    <span className="font-medium">{preview.data?.subject ?? subject}</span>
                  </div>
                  <div className="truncate">
                    <span className="text-[var(--color-foreground-muted)]">To: </span>
                    {to.length > 0 ? to.join(', ') : 'nobody yet'}
                    {cc.length > 0 ? (
                      <span className="text-[var(--color-foreground-muted)]"> · Cc {cc.join(', ')}</span>
                    ) : null}
                  </div>
                </div>
                {preview.data ? (
                  <iframe
                    title="Email preview"
                    srcDoc={preview.data.html}
                    // No scripts, no access to the app. Links may open in a new
                    // tab so the pay link can be checked from here.
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    className="h-[46vh] w-full bg-white lg:h-[56vh]"
                  />
                ) : preview.isError ? (
                  <p className="p-4 text-sm text-[var(--color-danger)]">{extractMessage(preview.error)}</p>
                ) : (
                  <div className="space-y-3 p-4">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] pt-3">
            <p className="text-xs text-[var(--color-foreground-muted)]">
              {error ? (
                <span className="text-[var(--color-danger)]">{error}</span>
              ) : to.length === 0 ? (
                'Pick at least one recipient.'
              ) : (
                <>
                  Sending to {to.length} {to.length === 1 ? 'address' : 'addresses'}
                  {cc.length > 0 ? `, ${cc.length} copied` : ''}.
                </>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={pending}>
                Cancel
              </Button>
              <Button onClick={send} disabled={pending || to.length === 0 || !draft.data}>
                <Mail className="size-4" /> {pending ? 'Sending…' : confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Field({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-muted)]">
        <span>{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}
